import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  WebcamComponent,
  WebcamImage,
  WebcamInitError,
  WebcamModule,
  WebcamUtil,
} from 'ngx-webcam';
import { ButtonModule } from 'primeng/button';
import { Observable, Subject } from 'rxjs';

@Component({
  selector: 'kon-camera',
  standalone: true,
  imports: [CommonModule, WebcamModule, FormsModule, ButtonModule],
  template: `
    <div *ngIf="visible()" class="relative w-full h-full bg-gray-900">
      {{ window.innerWidth }} x {{ window.innerHeight }}
      <webcam
        [trigger]="triggerObservable"
        (imageCapture)="handleImage($event)"
        [allowCameraSwitch]="false"
        [switchCamera]="nextWebcamObservable"
        [videoOptions]="videoOptions"
        (cameraSwitched)="cameraWasSwitched($event)"
        (initError)="handleInitError($event)"
        class="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-full"
      >
      </webcam>
      <p-button
        icon="pi pi-times"
        class="absolute top-4 left-4"
        styleClass="text-white bg-gray-900/50"
        [rounded]="true"
        severity="info"
        [outlined]="true"
        size="large"
        (click)="onClose.emit()"
      />
      <p-button
        class="absolute top-4 right-4 space-x-2"
        styleClass="text-white bg-gray-900/50"
        icon="pi pi-sync"
        [rounded]="true"
        severity="info"
        [outlined]="true"
        size="large"
        (click)="showNextWebcam(true)"
      />
      <p-button
        *ngIf="torchAvailable"
        class="absolute bottom-16 right-4 transform translate-y-1/2"
        styleClass="text-white bg-gray-900/50"
        icon="pi pi-bolt"
        [rounded]="true"
        severity="info"
        [outlined]="true"
        size="large"
        (click)="showNextWebcam(true)"
      />
      <p-button
        icon="pi pi-circle-fill text-3xl text-[3.5rem]"
        loadingIcon="pi pi-spin pi-spinner text-3xl"
        class="absolute bottom-16 left-1/2 transform -translate-x-1/2 translate-y-1/2"
        styleClass="w-20 h-20 border-2 text-white bg-gray-900/50"
        [rounded]="true"
        severity="info"
        [outlined]="true"
        size="large"
        (click)="triggerSnapshot()"
        [loading]="processing()"
      />
      <ng-content></ng-content>
    </div>
  `,
  styles: `
    :host::ng-deep webcam > .webcam-wrapper {
      width: 100%;
      height: 100%;
    }
    :host::ng-deep webcam > .webcam-wrapper > video {
      width: 100%;
      height: 100%;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CameraComponent {
  // toggle webcam on/off
  protected window = window;
  public multipleWebcamsAvailable = false;

  public deviceId?: string =
    localStorage.getItem('cameraDeviceId') ?? undefined;
  public mediaDevices: MediaDeviceInfo[] = [];
  public videoOptions: MediaTrackConstraints = {
    deviceId: this.deviceId
      ? {
          exact: this.deviceId,
        }
      : undefined,
    facingMode: 'environment',
  };
  public errors: WebcamInitError[] = [];

  @ViewChild('webcam') camera!: WebcamComponent;

  visible = input<boolean>();
  onSnapshot = output<WebcamImage>();
  onClose = output<void>();
  processing = input<boolean>();

  torchAvailable = false;

  // latest snapshot
  public webcamImage: WebcamImage | null = null;

  // webcam snapshot trigger
  private trigger: Subject<void> = new Subject<void>();
  // switch to next / previous / specific webcam; true/false: forward/backwards, string: deviceId
  private nextWebcam: Subject<boolean | string> = new Subject<
    boolean | string
  >();

  async ngOnInit() {
    this.mediaDevices = await WebcamUtil.getAvailableVideoInputs();
    this.multipleWebcamsAvailable =
      this.mediaDevices && this.mediaDevices.length > 1;
  }

  public triggerSnapshot(): void {
    this.trigger.next();
  }

  public handleInitError(error: WebcamInitError): void {
    this.errors.push(error);
  }

  toggleFlash() {
    this.videoOptions.autoGainControl;
  }

  public showNextWebcam(directionOrDeviceId?: boolean | string | null): void {
    // true => move forward through devices
    // false => move backwards through devices
    // string => move to device with given deviceId
    if (directionOrDeviceId !== undefined && directionOrDeviceId !== null) {
      this.nextWebcam.next(directionOrDeviceId);
    }
  }

  public handleImage(webcamImage: WebcamImage): void {
    this.webcamImage = webcamImage;
    this.onSnapshot.emit(webcamImage);
  }

  public cameraWasSwitched(deviceId: string): void {
    localStorage.setItem('cameraDeviceId', deviceId);
    this.deviceId = deviceId;
  }

  public get triggerObservable(): Observable<void> {
    return this.trigger.asObservable();
  }

  public get nextWebcamObservable(): Observable<boolean | string> {
    return this.nextWebcam.asObservable();
  }
}
